import { ActionButton } from "../../../components/Buttons";
import CardFrameSurface from "../../../components/CardFrameSurface";

function WallCardPreview({ card }) {
  const translations = Array.isArray(card.translations) && card.translations.length
    ? card.translations.slice(0, 2)
    : card.translation
      ? [{ text: card.translation }]
      : [];

  return (
    <CardFrameSurface className="mx-auto w-[min(72vw,270px)] drop-shadow-[0_24px_52px_rgba(0,0,0,0.5),0_0_34px_rgba(244,217,78,0.26)]">
      <div
        role="region"
        aria-label="Full card text"
        tabIndex={0}
        className="absolute inset-x-[13%] bottom-[18%] top-[16%] overflow-x-hidden overflow-y-auto overscroll-contain"
      >
        <div className="flex min-h-full flex-col items-center justify-start gap-2.5 py-1 text-center">
          <p
            className="w-full shrink-0 text-[17px] font-bold leading-tight text-[#2f2a16] [overflow-wrap:anywhere] sm:text-[19px]"
            dir="auto"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            {card.question}
          </p>

          {translations.length ? (
            <div className="flex w-full shrink-0 flex-col gap-2">
              {translations.map((translation, index) => (
                <p
                  key={`${translation.languageCode || translation.language || "translation"}-${index}`}
                  className="w-full shrink-0 text-[13px] font-medium leading-snug text-[#5f5942] [overflow-wrap:anywhere]"
                  dir="auto"
                  style={{ fontFamily: "'Poppins', sans-serif" }}
                >
                  {translation.text}
                </p>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </CardFrameSurface>
  );
}

function WallCardModalActions({
  card,
  onCreateCard,
  onShare,
  shareError,
  sharing,
  createCardPath,
}) {
  return (
    <div className="mx-auto flex w-full max-w-[17rem] flex-col gap-2">
      {createCardPath ? (
        <ActionButton
          href={createCardPath}
          onClick={() => onCreateCard(card)}
          fullWidth
          icon="plus"
          size="sm"
          aria-label="Create Card"
        >
          Create Card
        </ActionButton>
      ) : null}

      <ActionButton
        type="button"
        onClick={() => onShare(card)}
        disabled={sharing}
        fullWidth
        icon="share"
        size="sm"
        variant="secondary"
        aria-label={sharing ? "Preparing your link" : "Send Card"}
      >
        Send Card
      </ActionButton>

      {shareError ? (
        <p className="px-2 text-center font-sans text-xs font-bold leading-snug text-red-200">
          {shareError}
        </p>
      ) : null}
    </div>
  );
}

function WallCardModalTitleRow({
  wallTitle,
  showNavigation,
  canNavigatePrevious,
  canNavigateNext,
  nextNavigationLabel,
  onPreviousCard,
  onNextCard,
}) {
  return (
    <div className="mx-auto grid w-full max-w-[17rem] grid-cols-[2.25rem_minmax(0,1fr)_2.25rem] items-center">
      {showNavigation ? (
        <button
          type="button"
          onClick={onPreviousCard}
          disabled={!canNavigatePrevious}
          className="flex h-9 w-9 shrink-0 items-center justify-center justify-self-center rounded-full border border-white/12 bg-white/[0.06] p-0 text-center text-2xl leading-none text-white/76 shadow-[0_12px_30px_rgba(0,0,0,0.2)] backdrop-blur-xl transition hover:bg-white/[0.1] hover:text-white active:scale-95"
          aria-label="Previous card"
        >
          ‹
        </button>
      ) : (
        <span aria-hidden="true" />
      )}

      <h2
        id="wall-card-detail-title"
        className="font-sans text-xs font-bold tracking-wide text-white/85"
      >
        {wallTitle} Card
      </h2>

      {showNavigation ? (
        <button
          type="button"
          onClick={onNextCard}
          disabled={!canNavigateNext}
          className="flex h-9 w-9 shrink-0 items-center justify-center justify-self-center rounded-full border border-white/12 bg-white/[0.06] p-0 text-center text-2xl leading-none text-white/76 shadow-[0_12px_30px_rgba(0,0,0,0.2)] backdrop-blur-xl transition hover:bg-white/[0.1] hover:text-white active:scale-95"
          aria-label={nextNavigationLabel}
        >
          ›
        </button>
      ) : (
        <span aria-hidden="true" />
      )}
    </div>
  );
}

function WallCardDetailModal({
  card,
  wallTitle = "Community Wall",
  showNavigation,
  canNavigatePrevious,
  canNavigateNext,
  nextNavigationLabel,
  onClose,
  onPreviousCard,
  onNextCard,
  onCreateCard,
  onShare,
  shareError,
  sharing,
  createCardPath,
}) {
  if (!card) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-transparent px-5 py-8"
      role="dialog"
      aria-modal="true"
      aria-labelledby="wall-card-detail-title"
    >
      <div className="relative max-h-[90vh] w-full max-w-lg overflow-hidden rounded-[1.5rem] border border-[#f4d94e]/12 bg-black/[0.965] shadow-[0_0_24px_rgba(244,217,78,0.2),0_24px_62px_rgba(0,0,0,0.86)]">
        <ActionButton
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 z-20 h-9 w-9 rounded-full border-white/12 text-white shadow-[0_10px_24px_rgba(0,0,0,0.55)] hover:text-white"
          icon="x"
          size="icon"
          variant="darkSecondary"
          style={{ background: "#000000" }}
          aria-label="Close"
        />

        <div className="max-h-[90vh] overflow-y-auto p-4 text-white sm:p-5">
          <div className="relative z-10 space-y-4 pt-6 text-center">
            <WallCardPreview card={card} />

            <WallCardModalTitleRow
              wallTitle={wallTitle}
              showNavigation={showNavigation}
              canNavigatePrevious={canNavigatePrevious}
              canNavigateNext={canNavigateNext}
              nextNavigationLabel={nextNavigationLabel}
              onPreviousCard={onPreviousCard}
              onNextCard={onNextCard}
            />

            <WallCardModalActions
              card={card}
              onCreateCard={onCreateCard}
              onShare={onShare}
              shareError={shareError}
              sharing={sharing}
              createCardPath={createCardPath}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default WallCardDetailModal;
